import { useRef, useState } from 'react';

import type { InlineToolCall, Recommendation } from '../tools/types';
import type { Message, PageContext } from '../types';

export interface NextActionState {
  index: number;
  done: number[];
  dismissed: boolean;
  open: boolean;
}

export interface NextActionQueue {
  createdAt: string;
  id: number;
  recommendations: Recommendation[];
}

const FRESH: NextActionState = { index: 0, done: [], dismissed: false, open: true };

export function useNextActions() {
  const [states, setStates] = useState<Record<number, NextActionState>>({});

  const statesRef = useRef(states);
  statesRef.current = states;

  function stateFor(toolCallId: number) {
    return statesRef.current[toolCallId] || storedState(toolCallId);
  }

  function update(toolCallId: number, changes: Partial<NextActionState>) {
    const next = { ...stateFor(toolCallId), ...changes };

    statesRef.current = { ...statesRef.current, [toolCallId]: next };
    setStates(all => ({ ...all, [toolCallId]: next }));
    persistState(toolCallId, next);
  }

  function fold(messages: Message[]) {
    const latest = latestRecommendations(messages);

    if (latest) {
      update(latest.id, { open: false });
    }
  }

  // A folded card is still worth reporting, because sending is what folds it.
  function context(messages: Message[]): PageContext | null {
    const latest = latestRecommendations(messages);
    if (!latest) {
      return null;
    }

    const state = stateFor(latest.id);
    const { recommendation } = currentView(latest.recommendations, state);
    if (state.dismissed || !recommendation) {
      return null;
    }

    return {
      nextActionPosition: String(recommendation.position),
      nextActionTitle: recommendation.title,
      nextActionUrl: recommendation.url,
      ...(state.done.length > 0 && { nextActionsCleared: state.done.join(',') }),
    };
  }

  return { context, fold, stateFor, update };
}

export function mergePageContext(page: PageContext | null, nextAction: PageContext | null) {
  return page || nextAction ? { ...page, ...nextAction } : null;
}

export function latestRecommendations(messages: Message[]) {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const queue = recommendationQueue(messages[index].inlineToolCalls || []);

    if (queue) {
      return queue.recommendations.length > 0 && raisedToday(queue.createdAt) ? queue : null;
    }
  }

  return null;
}

export function recommendationQueue(toolCalls: InlineToolCall[]) {
  return toolCalls.reduce<NextActionQueue | null>((found, call) => {
    if (call.name !== 'recommendations') {
      return found;
    }

    return {
      createdAt: call.createdAt,
      id: call.id,
      recommendations: (found?.recommendations || []).concat(call.metadata.recommendations || []),
    };
  }, null);
}

export function currentView(recommendations: Recommendation[], state: NextActionState) {
  const remaining = recommendations.filter(candidate => !state.done.includes(candidate.position));
  const index = Math.min(state.index, Math.max(0, remaining.length - 1));

  return { index, recommendation: remaining[index], remaining };
}

export function cleared(queue: NextActionQueue, state: NextActionState) {
  return state.dismissed || currentView(queue.recommendations, state).remaining.length === 0;
}

function raisedToday(createdAt: string) {
  return new Date(createdAt).toDateString() === new Date().toDateString();
}

function storedState(toolCallId: number): NextActionState {
  try {
    const stored = window.sessionStorage.getItem(storageKey(toolCallId));
    return stored ? { ...FRESH, ...JSON.parse(stored) as Partial<NextActionState> } : FRESH;
  } catch {
    return FRESH;
  }
}

function persistState(toolCallId: number, state: NextActionState) {
  try {
    window.sessionStorage.setItem(storageKey(toolCallId), JSON.stringify(state));
  } catch {
    // The card forgets where it was when the page reloads.
  }
}

function storageKey(toolCallId: number) {
  return `ai-assistant-next-actions-${toolCallId}`;
}
